import { debugConfig } from './debug-config';
import { logWithContext, LogLevel } from './errorReporting';

// Performance profiling
export class PerformanceProfiler {
  private static marks = new Map<string, number>();
  private static measures = new Map<string, { start: number; end?: number; duration?: number }>();

  static mark(name: string) {
    const time = performance.now();
    this.marks.set(name, time);
    
    if (debugConfig.getConfig().logPerformance) {
      logWithContext(LogLevel.DEBUG, `Performance mark: ${name}`, {
        component: 'Performance',
        action: 'performance-mark',
        data: { mark: name, time },
      });
    }
  }

  static measure(name: string, startMark: string, endMark?: string) {
    const start = this.marks.get(startMark);
    const end = endMark ? this.marks.get(endMark) : performance.now();
    
    if (!start) {
      console.warn(`Start mark "${startMark}" not found`);
      return;
    }
    
    const duration = (end || performance.now()) - start;
    
    this.measures.set(name, {
      start,
      end: end || performance.now(),
      duration,
    });
    
    if (debugConfig.getConfig().logPerformance) {
      logWithContext(LogLevel.INFO, `Performance measure: ${name}`, {
        component: 'Performance',
        action: 'performance-measure',
        data: { 
          measure: name, 
          duration: `${duration.toFixed(2)}ms`,
          startMark,
          endMark,
        },
      });
    }
    
    return duration;
  }

  static getMeasures() {
    return Array.from(this.measures.entries()).map(([name, data]) => ({
      name,
      ...data,
    }));
  }

  static clear() {
    this.marks.clear();
    this.measures.clear();
  }
}

// Memory leak detection
export class MemoryMonitor {
  private static snapshots: Array<{ timestamp: Date; usedJSHeapSize: number; totalJSHeapSize: number }> = [];
  private static interval: NodeJS.Timeout | null = null;

  static start(intervalMs: number = 10000) {
    if (this.interval) {
      console.warn('Memory monitor already running');
      return;
    }
    
    if (!('memory' in performance)) {
      console.warn('Performance memory API not available');
      return;
    }
    
    this.interval = setInterval(() => {
      const memory = (performance as any).memory;
      const snapshot = {
        timestamp: new Date(),
        usedJSHeapSize: memory.usedJSHeapSize,
        totalJSHeapSize: memory.totalJSHeapSize,
      };
      
      this.snapshots.push(snapshot);
      
      // Keep only last 100 snapshots
      if (this.snapshots.length > 100) {
        this.snapshots.shift();
      }
      
      // Check for potential memory leak
      if (this.snapshots.length >= 5) {
        const recent = this.snapshots.slice(-5);
        const increasing = recent.every((snap, i) => 
          i === 0 || snap.usedJSHeapSize > recent[i - 1].usedJSHeapSize
        );
        
        if (increasing) {
          const increase = recent[4].usedJSHeapSize - recent[0].usedJSHeapSize;
          const increasePercentage = (increase / recent[0].usedJSHeapSize) * 100;
          
          if (increasePercentage > 20) {
            logWithContext(LogLevel.WARN, 'Potential memory leak detected', {
              component: 'MemoryMonitor',
              action: 'memory-leak-warning',
              data: {
                increaseBytes: increase,
                increasePercentage: `${increasePercentage.toFixed(2)}%`,
                currentUsage: `${(recent[4].usedJSHeapSize / 1024 / 1024).toFixed(2)}MB`,
              },
            });
          }
        }
      }
    }, intervalMs);
    
    logWithContext(LogLevel.INFO, 'Memory monitor started', {
      component: 'MemoryMonitor',
      action: 'start',
      data: { intervalMs },
    });
  }

  static stop() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
      
      logWithContext(LogLevel.INFO, 'Memory monitor stopped', {
        component: 'MemoryMonitor',
        action: 'stop',
      });
    }
  }

  static getSnapshots() {
    return [...this.snapshots];
  }

  static getCurrentUsage() {
    if (!('memory' in performance)) {
      return null;
    }
    
    const memory = (performance as any).memory;
    return {
      usedJSHeapSize: memory.usedJSHeapSize,
      totalJSHeapSize: memory.totalJSHeapSize,
      jsHeapSizeLimit: memory.jsHeapSizeLimit,
      usedJSHeapSizeMB: (memory.usedJSHeapSize / 1024 / 1024).toFixed(2),
      totalJSHeapSizeMB: (memory.totalJSHeapSize / 1024 / 1024).toFixed(2),
      jsHeapSizeLimitMB: (memory.jsHeapSizeLimit / 1024 / 1024).toFixed(2),
    };
  }
}

// State snapshot utility
export class StateSnapshot {
  private static snapshots = new Map<string, any>();

  static capture(name: string, state: any) {
    const snapshot = {
      timestamp: new Date(),
      state: this.deepClone(state),
    };
    
    this.snapshots.set(name, snapshot);
    
    logWithContext(LogLevel.DEBUG, `State snapshot captured: ${name}`, {
      component: 'StateSnapshot',
      action: 'capture',
      data: { name, size: JSON.stringify(snapshot.state).length },
    });
  }

  static compare(name1: string, name2: string) {
    const snapshot1 = this.snapshots.get(name1);
    const snapshot2 = this.snapshots.get(name2);
    
    if (!snapshot1 || !snapshot2) {
      console.error('One or both snapshots not found');
      return null;
    }
    
    const diff = this.deepDiff(snapshot1.state, snapshot2.state);
    
    logWithContext(LogLevel.INFO, `State comparison: ${name1} vs ${name2}`, {
      component: 'StateSnapshot',
      action: 'compare',
      data: { diff },
    });
    
    return diff;
  }

  static get(name: string) {
    return this.snapshots.get(name);
  }

  static list() {
    return Array.from(this.snapshots.keys());
  }

  static clear(name?: string) {
    if (name) {
      this.snapshots.delete(name);
    } else {
      this.snapshots.clear();
    }
  }

  private static deepClone(obj: any): any {
    if (obj === null || typeof obj !== 'object') return obj;
    if (obj instanceof Date) return new Date(obj.getTime());
    if (obj instanceof Array) return obj.map(item => this.deepClone(item));
    if (obj instanceof Object) {
      const clonedObj: any = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          clonedObj[key] = this.deepClone(obj[key]);
        }
      }
      return clonedObj;
    }
  }

  private static deepDiff(obj1: any, obj2: any, path: string = ''): any[] {
    const differences: any[] = [];
    
    if (obj1 === obj2) return differences;
    
    if (typeof obj1 !== typeof obj2) {
      differences.push({
        path,
        type: 'type',
        oldValue: obj1,
        newValue: obj2,
      });
      return differences;
    }
    
    if (typeof obj1 !== 'object' || obj1 === null || obj2 === null) {
      if (obj1 !== obj2) {
        differences.push({
          path,
          type: 'value',
          oldValue: obj1,
          newValue: obj2,
        });
      }
      return differences;
    }
    
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    const allKeys = new Set([...keys1, ...keys2]);
    
    for (const key of allKeys) {
      const newPath = path ? `${path}.${key}` : key;
      
      if (!(key in obj1)) {
        differences.push({
          path: newPath,
          type: 'added',
          newValue: obj2[key],
        });
      } else if (!(key in obj2)) {
        differences.push({
          path: newPath,
          type: 'removed',
          oldValue: obj1[key],
        });
      } else {
        differences.push(...this.deepDiff(obj1[key], obj2[key], newPath));
      }
    }
    
    return differences;
  }
}

// Debug console commands
export function setupDebugCommands() {
  if (typeof window === 'undefined') return;
  
  // Add debug commands to window
  (window as any).debug = {
    // Toggle debug mode
    enable: () => {
      debugConfig.updateConfig({ enabled: true, showDebugPanel: true });
      console.log('Debug mode enabled');
    },
    disable: () => {
      debugConfig.updateConfig({ enabled: false });
      console.log('Debug mode disabled');
    },
    
    // Performance profiling
    performance: {
      mark: (name: string) => PerformanceProfiler.mark(name),
      measure: (name: string, start: string, end?: string) => PerformanceProfiler.measure(name, start, end),
      getMeasures: () => PerformanceProfiler.getMeasures(),
      clear: () => PerformanceProfiler.clear(),
    },
    
    // Memory monitoring
    memory: {
      start: (interval?: number) => MemoryMonitor.start(interval),
      stop: () => MemoryMonitor.stop(),
      current: () => MemoryMonitor.getCurrentUsage(),
      snapshots: () => MemoryMonitor.getSnapshots(),
    },
    
    // State snapshots
    state: {
      capture: (name: string, state: any) => StateSnapshot.capture(name, state),
      compare: (name1: string, name2: string) => StateSnapshot.compare(name1, name2),
      get: (name: string) => StateSnapshot.get(name),
      list: () => StateSnapshot.list(),
      clear: (name?: string) => StateSnapshot.clear(name),
    },
    
    // Logs
    logs: {
      get: () => debugConfig.getLogs(),
      clear: () => debugConfig.clearLogs(),
      export: () => debugConfig.exportLogs(),
      download: () => debugConfig.downloadLogs(),
    },
    
    // Config
    config: {
      get: () => debugConfig.getConfig(),
      update: (updates: any) => debugConfig.updateConfig(updates),
    },
    
    // Help
    help: () => {
      console.log(`
Debug Console Commands:
======================

debug.enable()                    - Enable debug mode
debug.disable()                   - Disable debug mode

Performance:
debug.performance.mark(name)      - Create performance mark
debug.performance.measure(name, start, end?) - Measure between marks
debug.performance.getMeasures()   - Get all measurements
debug.performance.clear()         - Clear all marks and measures

Memory:
debug.memory.start(interval?)     - Start memory monitoring
debug.memory.stop()               - Stop memory monitoring
debug.memory.current()            - Get current memory usage
debug.memory.snapshots()          - Get memory snapshots

State:
debug.state.capture(name, state)  - Capture state snapshot
debug.state.compare(name1, name2) - Compare two snapshots
debug.state.get(name)             - Get specific snapshot
debug.state.list()                - List all snapshots
debug.state.clear(name?)          - Clear snapshot(s)

Logs:
debug.logs.get()                  - Get all logs
debug.logs.clear()                - Clear all logs
debug.logs.export()               - Export logs as JSON
debug.logs.download()             - Download logs file

Config:
debug.config.get()                - Get current config
debug.config.update(updates)      - Update config

debug.help()                      - Show this help message
      `);
    },
  };
  
  console.log('Debug commands loaded. Type "debug.help()" for available commands.');
}

// Network request monitor
export function monitorNetworkRequests() {
  if (!debugConfig.isEnabled()) return;
  
  // Monitor fetch requests (already handled by debug-api-client)
  
  // Monitor XMLHttpRequest
  const originalXHR = window.XMLHttpRequest;
  
  window.XMLHttpRequest = function() {
    const xhr = new originalXHR();
    const originalOpen = xhr.open;
    const originalSend = xhr.send;
    
    let method: string;
    let url: string;
    let startTime: number;
    
    xhr.open = function(...args: any[]) {
      method = args[0];
      url = args[1];
      return originalOpen.apply(xhr, args as any);
    };
    
    xhr.send = function(body?: any) {
      startTime = Date.now();
      
      xhr.addEventListener('loadend', () => {
        const duration = Date.now() - startTime;
        
        logWithContext(
          xhr.status >= 400 ? LogLevel.ERROR : LogLevel.DEBUG,
          `XHR ${method} ${url} - ${xhr.status} (${duration}ms)`,
          {
            component: 'NetworkMonitor',
            action: 'xhr-request',
            data: {
              method,
              url,
              status: xhr.status,
              duration,
              responseText: xhr.responseText?.substring(0, 500),
            },
          }
        );
      });
      
      return originalSend.call(xhr, body);
    };
    
    return xhr;
  } as any;
}